import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Building2, MapPin, Ruler, Calendar, Layers, ArrowRight, Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

export default function BrokerPage() {
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // demand selected for response
  const [openMode, setOpenMode] = useState(false); // open submission
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/demands").then((r) => setDemands(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white" data-testid="broker-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50/70 via-white to-white" />
        <div className="container-zelo py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-neutral-200 text-xs font-semibold tracking-[0.2em] uppercase text-brand">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" /> Broker · Demand Board
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-medium tracking-tight mt-6 text-balance">
              Have something interesting? <span className="text-brand">We have brands waiting.</span>
            </h1>
            <p className="mt-5 text-lg text-neutral-500 leading-relaxed">
              Two ways to work with Zelo. Pick a live brand mandate below and submit a matching property — or send us any space worth our attention and we'll bring the right brand to it. Either way: standard market brokerage, paid clean, on a timeline you can plan around.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#match" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="broker-cta-match">
                Match an Open Demand <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#submit" className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-6 py-3 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="broker-cta-open">
                Submit an Open Property <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DEMANDS LIST */}
      <section id="match" className="container-zelo pb-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-tiny text-brand">Open Mandates</div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight mt-1">{loading ? "Loading…" : `${demands.length} live demand${demands.length === 1 ? "" : "s"}`}</h2>
          </div>
          <a href="#submit" className="hidden md:inline-flex items-center gap-2 rounded-full border border-neutral-200 px-5 py-2.5 text-sm font-semibold hover:border-brand hover:text-brand transition">
            Submit an Open Property <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="broker-demands-grid">
          {demands.map((d) => (
            <div key={d.id} className="rounded-2xl border border-neutral-200 bg-white p-6 hover:-translate-y-1 hover:shadow-soft transition-all" data-testid={`demand-${d.demand_code}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold tracking-[0.18em] uppercase text-brand">{d.demand_code}</span>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Live</span>
              </div>
              <div className="font-heading text-xl font-medium mt-3">{d.category}</div>
              <div className="flex items-center gap-1 text-sm text-neutral-500 mt-1.5"><MapPin className="w-3.5 h-3.5" /> {d.city}, {d.state}</div>

              {d.preferred_areas?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {d.preferred_areas.map((a, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-brand-50 text-brand font-medium">{a}</span>
                  ))}
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <Mini Icon={Ruler} l="Carpet" v={`${d.carpet_min || "—"}–${d.carpet_max || "—"} sqft`} />
                <Mini Icon={Layers} l="Floor" v={d.floor_pref || "Any"} />
                <Mini Icon={Building2} l="Frontage" v={d.frontage_min_ft ? `${d.frontage_min_ft}+ ft` : "—"} />
                <Mini Icon={Calendar} l="Possession" v={d.possession_by || "Flexible"} />
              </div>

              {d.notes && <p className="mt-4 text-sm text-neutral-600 leading-relaxed line-clamp-3">{d.notes}</p>}

              <button
                onClick={() => { setActive(d); setOpenMode(false); setSubmitted(false); document.getElementById("submit")?.scrollIntoView({ behavior: "smooth" }); }}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand/30 text-brand px-4 py-2.5 text-sm font-semibold hover:bg-brand hover:text-white transition"
                data-testid={`demand-respond-btn-${d.demand_code}`}
              >
                I Have a Match <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {!loading && demands.length === 0 && (
          <div className="rounded-2xl border border-dashed border-neutral-200 p-12 text-center">
            <div className="font-heading text-2xl font-medium">No open mandates right now.</div>
            <div className="mt-2 text-neutral-500">Submit a property below — we'll route it to the right brand when one comes in.</div>
          </div>
        )}
      </section>

      {/* SUBMIT FORM */}
      <section id="submit" className="bg-neutral-50/60 py-20 md:py-24">
        <div className="container-zelo max-w-3xl">
          <div className="text-center mb-10">
            <div className="label-tiny text-brand">{active ? "Submit a Match" : openMode ? "Open Submission" : "Submit a Match or an Open Property"}</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">
              {active ? `Match for ${active.demand_code}` : "Tell us about the property."}
            </h2>
            <p className="mt-3 text-neutral-500">
              {active
                ? `${active.category} · ${active.city}, ${active.state}`
                : "Pick a live demand from the board, or submit any space worth our attention — we'll bring the right brand to it."}
            </p>
            {!active && (
              <div className="mt-6 inline-flex bg-white border border-neutral-200 rounded-full p-1" data-testid="broker-mode-toggle">
                <button onClick={() => setOpenMode(false)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${!openMode ? "bg-brand text-white" : "text-neutral-600 hover:text-brand"}`} data-testid="broker-mode-match">
                  Match a Demand
                </button>
                <button onClick={() => setOpenMode(true)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${openMode ? "bg-brand text-white" : "text-neutral-600 hover:text-brand"}`} data-testid="broker-mode-open">
                  Open Property
                </button>
              </div>
            )}
          </div>
          <BrokerForm active={active} clearActive={() => setActive(null)} submitted={submitted} setSubmitted={setSubmitted} demands={demands} openMode={openMode} setOpenMode={setOpenMode} />
        </div>
      </section>
    </div>
  );
}

function Mini({ Icon, l, v }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-neutral-400" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-semibold">{l}</div>
        <div className="font-medium text-neutral-900 text-sm">{v}</div>
      </div>
    </div>
  );
}

function BrokerForm({ active, clearActive, submitted, setSubmitted, demands, openMode, setOpenMode }) {
  const [form, setForm] = useState({
    demand_id: "", broker_name: "", broker_phone: "", broker_email: "", broker_company: "",
    property_city: "", property_area: "", property_carpet: "", property_floor: "",
    property_rent_psf: "", property_possession: "", notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (active) setForm((f) => ({ ...f, demand_id: active.id, property_city: active.city }));
  }, [active]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!openMode && !active && !form.demand_id) {
      toast.error("Pick a demand to match, or switch to Open Property");
      return;
    }
    if (!form.broker_name || !form.broker_phone || !form.property_city || !form.property_area) {
      toast.error("Name, phone, city, and area are required"); return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        demand_id: openMode ? null : (form.demand_id || null),
        submission_type: openMode ? "open" : "matched",
        property_carpet: form.property_carpet ? parseFloat(form.property_carpet) : null,
        property_rent_psf: form.property_rent_psf ? parseFloat(form.property_rent_psf) : null,
      };
      await api.post("/demands/respond", payload);
      setSubmitted(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Submission failed"); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-10 text-center" data-testid="broker-submit-success">
        <div className="w-16 h-16 rounded-full bg-brand-50 text-brand mx-auto flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
        <div className="font-heading text-2xl font-medium">Submission received.</div>
        <div className="text-neutral-500 mt-2">Our acquisition team reviews every response within 48 hours. If your property fits a brand brief — current or upcoming — you'll get a verification call.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-neutral-200 bg-white p-8 md:p-10 space-y-5" data-testid="broker-form">
      {!openMode && (
        <div>
          <div className="label-tiny mb-1.5">Responding to demand</div>
          <select className={inp} value={form.demand_id} onChange={(e) => set("demand_id", e.target.value)} data-testid="broker-demand-select">
            <option value="">— Pick a demand —</option>
            {demands.map((d) => <option key={d.id} value={d.id}>{d.demand_code} · {d.category} · {d.city}</option>)}
          </select>
        </div>
      )}
      {openMode && (
        <div className="rounded-xl bg-brand-50 border border-brand/20 p-4 text-sm text-brand-800">
          <strong className="font-semibold">Open submission</strong> — share any retail property you think Zelo should know about. We'll match it with a current or upcoming brand brief.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Your Name *"><input className={inp} value={form.broker_name} onChange={(e) => set("broker_name", e.target.value)} data-testid="broker-name" /></Field>
        <Field label="Phone *"><input className={inp} value={form.broker_phone} onChange={(e) => set("broker_phone", e.target.value)} data-testid="broker-phone" /></Field>
        <Field label="Email"><input type="email" className={inp} value={form.broker_email} onChange={(e) => set("broker_email", e.target.value)} data-testid="broker-email" /></Field>
        <Field label="Company / Firm"><input className={inp} value={form.broker_company} onChange={(e) => set("broker_company", e.target.value)} data-testid="broker-company" /></Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Property City *"><input className={inp} value={form.property_city} onChange={(e) => set("property_city", e.target.value)} data-testid="broker-property-city" /></Field>
        <Field label="Area / Locality *"><input className={inp} value={form.property_area} onChange={(e) => set("property_area", e.target.value)} data-testid="broker-property-area" /></Field>
        <Field label="Carpet Area (sqft)"><input className={inp} value={form.property_carpet} onChange={(e) => set("property_carpet", e.target.value)} data-testid="broker-property-carpet" /></Field>
        <Field label="Floor (e.g., Ground + First)"><input className={inp} value={form.property_floor} onChange={(e) => set("property_floor", e.target.value)} data-testid="broker-property-floor" /></Field>
        <Field label="Rent ₹/sqft"><input className={inp} value={form.property_rent_psf} onChange={(e) => set("property_rent_psf", e.target.value)} data-testid="broker-property-rent" /></Field>
        <Field label="Possession (Mon YYYY)"><input className={inp} value={form.property_possession} onChange={(e) => set("property_possession", e.target.value)} data-testid="broker-property-possession" /></Field>
      </div>
      <Field label={openMode ? "Why this property is interesting" : "Notes / Match Highlights"}><textarea rows={4} className={inp} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="broker-notes" /></Field>
      <div className="flex justify-between items-center pt-2">
        {active && <button type="button" onClick={clearActive} className="text-sm text-neutral-500 hover:text-brand">← Clear demand selection</button>}
        <button disabled={loading} className="ml-auto inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95 disabled:opacity-60" data-testid="broker-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit {openMode ? "Property" : "Match"} <Send className="w-4 h-4" /></>}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-neutral-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm bg-white";
